import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Video, VideoOff, Layers, Box, Cog, Hand, ZoomIn, Move, Pointer,
  RotateCcw, Ruler, Folder, RefreshCw, AlertCircle, Loader2, X,
  RotateCw, ZoomOut, ArrowLeftRight, TestTube2, Sparkles, Car, Play, Pause, Zap, Map, Scissors
} from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import Viewer3D from './Viewer3D';
import MeasurementPanel from './MeasurementPanel';
import ExplodePanel from './ExplodePanel';
import ModelBrowser from './ModelBrowser';
import GeminiPanel from './GeminiPanel';
import GuidedTour from './GuidedTour';
import PartIdentifier from './PartIdentifier';
import SectionPanel from './SectionPanel';
import { HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Model URLs (served from public folder to avoid CORS)
const CAR_ENGINE_URL = '/car_engine_assembly.glb';
const V8_ENGINE_URL = '/v8_engine.glb';

// Gesture types
const GestureType = {
  NONE: 'none',
  OPEN_PALM: 'open_palm',
  CLOSED_FIST: 'closed_fist',
  PINCH: 'pinch',
  POINTING: 'pointing',
  SPREAD: 'spread'
};

// ============== EXPONENTIAL SMOOTHING CLASS ==============
class ExponentialSmoother {
  constructor(alpha = 0.3) {
    this.alpha = alpha;
    this.values = { x: 0, y: 0, zoom: 0, spin: 0 };
    this.initialized = false;
  }
  
  update(raw) {
    if (!this.initialized) {
      this.values = { ...raw };
      this.initialized = true;
      return this.values;
    }
    
    // Exponential moving average: new = alpha * raw + (1-alpha) * old
    this.values.x = this.alpha * raw.x + (1 - this.alpha) * this.values.x;
    this.values.y = this.alpha * raw.y + (1 - this.alpha) * this.values.y;
    this.values.zoom = this.alpha * raw.zoom + (1 - this.alpha) * this.values.zoom;
    this.values.spin = this.alpha * raw.spin + (1 - this.alpha) * this.values.spin;
    
    return { ...this.values };
  }
  
  reset() {
    this.values = { x: 0, y: 0, zoom: 0, spin: 0 };
    this.initialized = false;
  }
}

// ============== GESTURE RECOGNITION ==============
const recognizeGesture = (landmarks) => {
  if (!landmarks || landmarks.length < 21) {
    return { gesture: GestureType.NONE, confidence: 0, fingerSpread: 0 };
  }
  
  const wrist = landmarks[0];
  const thumbTip = landmarks[4];
  const thumbIP = landmarks[3];
  const indexTip = landmarks[8];
  const indexDIP = landmarks[7];
  const indexPIP = landmarks[6];
  const middleTip = landmarks[12];
  const middlePIP = landmarks[10];
  const ringTip = landmarks[16];
  const ringPIP = landmarks[14];
  const pinkyTip = landmarks[20];
  const pinkyPIP = landmarks[18];
  
  const thumbMCP = landmarks[2];
  
  // Calculate distances
  const thumbIndexDist = Math.hypot(thumbTip.x - indexTip.x, thumbTip.y - indexTip.y);
  const fingerSpread = Math.hypot(indexTip.x - pinkyTip.x, indexTip.y - pinkyTip.y);
  
  // Check if each finger is extended (tip is above PIP joint - lower y = higher on screen)
  const thumbExtended = Math.abs(thumbTip.x - wrist.x) > Math.abs(thumbMCP.x - wrist.x);
  const indexExtended = indexTip.y < indexPIP.y - 0.03;
  const middleExtended = middleTip.y < middlePIP.y - 0.03;
  const ringExtended = ringTip.y < ringPIP.y - 0.03;
  const pinkyExtended = pinkyTip.y < pinkyPIP.y - 0.03;
  
  const extendedCount = [thumbExtended, indexExtended, middleExtended, ringExtended, pinkyExtended].filter(Boolean).length;
  
  // Check if fingers are curled (for fist detection)
  const indexCurled = indexTip.y > indexPIP.y + 0.02;
  const middleCurled = middleTip.y > middlePIP.y + 0.02;
  const ringCurled = ringTip.y > ringPIP.y + 0.02;
  const pinkyCurled = pinkyTip.y > pinkyPIP.y + 0.02;
  
  // Check thumb position relative to index for pinch
  // Pinch requires thumb tip close to index tip AND both extended forward
  const thumbIndexAngle = Math.atan2(thumbTip.y - indexTip.y, thumbTip.x - indexTip.x);
  const thumbForward = thumbTip.y < thumbIP.y; // Thumb pointing up/forward
  
  // POINTING: Only index extended, all others curled, thumb can be in any position
  // Key: middle, ring, pinky must NOT be extended
  if (indexExtended && !middleExtended && !ringExtended && !pinkyExtended) {
    // Additional check: index finger should be relatively straight and pointing
    const indexStraight = indexTip.y < indexDIP.y - 0.01;
    if (indexStraight) {
      // Make sure thumb is not close to index (that would be pinch)
      if (thumbIndexDist > 0.1) {
        return { gesture: GestureType.POINTING, confidence: 0.95, fingerSpread };
      }
    }
  }
  
  // PINCH: thumb tip very close to index tip (both need to be somewhat extended/forward)
  // Stricter threshold and require other fingers to not be extended
  if (thumbIndexDist < 0.05 && !middleExtended && !ringExtended && !pinkyExtended) {
    return { gesture: GestureType.PINCH, confidence: 1 - thumbIndexDist * 15, fingerSpread };
  }
  
  // SPREAD: all fingers extended AND spread wide apart
  if (extendedCount >= 5 && fingerSpread > 0.28) {
    return { gesture: GestureType.SPREAD, confidence: fingerSpread, fingerSpread };
  }
  
  // CLOSED FIST: most fingers curled (stricter detection)
  const curledCount = [indexCurled, middleCurled, ringCurled, pinkyCurled].filter(Boolean).length;
  if (curledCount >= 3 && extendedCount <= 1) {
    return { gesture: GestureType.CLOSED_FIST, confidence: curledCount / 4, fingerSpread };
  }
  
  // OPEN PALM: 4-5 fingers extended, not spread wide (for rotation)
  if (extendedCount >= 4 && fingerSpread <= 0.28) {
    return { gesture: GestureType.OPEN_PALM, confidence: extendedCount / 5, fingerSpread };
  }
  
  return { gesture: GestureType.NONE, confidence: 0, fingerSpread };
};

// ============== CAMERA PERMISSION MODAL ==============
const CameraPermissionModal = ({ isOpen, onClose, onRequestPermission, onRetry, status, error, availableCameras, selectedCameraId, onCameraSelect }) => {
  const [showCameraList, setShowCameraList] = React.useState(false);
  
  // Enumerate cameras when modal opens
  React.useEffect(() => {
    if (isOpen && availableCameras.length === 0) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        // This will be handled by the parent
      }).catch(() => {});
    }
  }, [isOpen, availableCameras.length]);
  
  return (
  <Dialog open={isOpen} onOpenChange={() => {}}>
    <DialogContent className="sm:max-w-md" data-testid="camera-permission-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Hand className="w-5 h-5 text-cyan-400" />
          Enable Gesture Controls
        </DialogTitle>
        <DialogDescription>
          Control your 3D models with hand gestures. Allow camera access to get started.
        </DialogDescription>
      </DialogHeader>
      
      <div className="space-y-4 py-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          {[
            { icon: <Hand className="w-4 h-4" />, label: 'Open Palm', action: 'Rotate view', color: 'bg-cyan-500/20' },
            { icon: '🤏', label: 'Pinch', action: 'Zoom in/out', color: 'bg-green-500/20' },
            { icon: '✊', label: 'Fist', action: 'Pan view', color: 'bg-orange-500/20' },
            { icon: '👆', label: 'Point', action: 'Select parts', color: 'bg-purple-500/20' }
          ].map(({ icon, label, action, color }) => (
            <div key={label} className={`flex items-center gap-2 p-2 rounded-lg ${color}`}>
              <div className="w-8 h-8 rounded-full bg-background/50 flex items-center justify-center">
                {typeof icon === 'string' ? <span className="text-lg">{icon}</span> : icon}
              </div>
              <div>
                <div className="font-medium text-foreground">{label}</div>
                <div className="text-xs text-muted-foreground">{action}</div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Camera Selection */}
        {availableCameras.length > 1 && (
          <div className="p-3 rounded-lg bg-white/5 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Select Camera
              </span>
              <button 
                onClick={() => setShowCameraList(!showCameraList)}
                className="text-xs text-cyan-400 hover:text-cyan-300"
              >
                {showCameraList ? 'Hide' : 'Change'}
              </button>
            </div>
            {showCameraList ? (
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {availableCameras.map((camera, index) => (
                  <button
                    key={camera.deviceId}
                    onClick={() => {
                      onCameraSelect(camera.deviceId);
                      setShowCameraList(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
                      selectedCameraId === camera.deviceId 
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                        : 'hover:bg-white/10 text-gray-300'
                    }`}
                  >
                    {camera.label || `Camera ${index + 1}`}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-white">
                {availableCameras.find(c => c.deviceId === selectedCameraId)?.label || 
                 availableCameras[0]?.label || 
                 'Default Camera'}
              </div>
            )}
          </div>
        )}
        
        {status === 'loading' && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Requesting camera access...</span>
          </div>
        )}
        
        {status === 'error' && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
            <div className="flex items-center gap-2 text-destructive mb-2">
              <AlertCircle className="w-4 h-4" />
              <span className="font-medium">Camera Access Failed</span>
            </div>
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}
      </div>
      
      <DialogFooter className="flex gap-2">
        <Button variant="ghost" onClick={onClose} data-testid="skip-camera-button">
          Use Mouse Only
        </Button>
        {status === 'error' ? (
          <Button onClick={onRetry} data-testid="retry-camera-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        ) : (
          <Button onClick={onRequestPermission} disabled={status === 'loading'} data-testid="enable-camera-button" className="bg-cyan-600 hover:bg-cyan-700">
            {status === 'loading' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
            Enable Camera
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
};

// ============== WEBCAM PREVIEW (CIRCULAR) ==============
const WebcamPreview = ({ videoRef, canvasRef, isActive, landmarks, gesture, status, onClose, fps, hoveredPart, availableCameras, selectedCameraId, onCameraChange, showCameraSelector, setShowCameraSelector }) => {
  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    canvas.width = 280;
    canvas.height = 280;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (!landmarks || landmarks.length === 0) return;
    
    const connections = [
      [0,1],[1,2],[2,3],[3,4],
      [0,5],[5,6],[6,7],[7,8],
      [0,9],[9,10],[10,11],[11,12],
      [0,13],[13,14],[14,15],[15,16],
      [0,17],[17,18],[18,19],[19,20],
      [5,9],[9,13],[13,17]
    ];
    
    // Glow effect
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 15;
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    
    connections.forEach(([start, end]) => {
      const s = landmarks[start];
      const e = landmarks[end];
      if (s && e) {
        ctx.beginPath();
        ctx.moveTo(s.x * canvas.width, s.y * canvas.height);
        ctx.lineTo(e.x * canvas.width, e.y * canvas.height);
        ctx.stroke();
      }
    });
    
    ctx.shadowBlur = 0;
    
    landmarks.forEach((lm, i) => {
      const x = lm.x * canvas.width;
      const y = lm.y * canvas.height;
      
      if ([4, 8, 12, 16, 20].includes(i)) {
        ctx.fillStyle = i === 8 ? '#ff00ff' : '#2ED573'; // Index tip is magenta
        ctx.beginPath();
        ctx.arc(x, y, i === 8 ? 12 : 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (i === 0) {
        ctx.fillStyle = '#F59E0B';
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = '#0066CC';
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw laser line from index tip if pointing
    if (gesture === GestureType.POINTING) {
      const indexTip = landmarks[8];
      const indexDip = landmarks[7];
      if (indexTip && indexDip) {
        const dx = indexTip.x - indexDip.x;
        const dy = indexTip.y - indexDip.y;
        const len = Math.hypot(dx, dy);
        if (len > 0) {
          const endX = indexTip.x + (dx / len) * 0.5;
          const endY = indexTip.y + (dy / len) * 0.5;
          
          ctx.shadowColor = '#ff00ff';
          ctx.shadowBlur = 20;
          ctx.strokeStyle = '#ff00ff';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(indexTip.x * canvas.width, indexTip.y * canvas.height);
          ctx.lineTo(endX * canvas.width, endY * canvas.height);
          ctx.stroke();
        }
      }
    }
  }, [landmarks, canvasRef, gesture]);
  
  const getStatusColor = () => {
    if (!isActive) return 'border-gray-600';
    if (status === 'tracking') return 'border-cyan-400';
    if (status === 'ready') return 'border-blue-500';
    if (status === 'loading') return 'border-yellow-500';
    return 'border-gray-600';
  };
  
  return (
    <motion.div 
      className={`fixed right-4 bottom-20 w-72 h-72 rounded-full overflow-hidden border-4 ${getStatusColor()} bg-black z-40`}
      style={{ 
        boxShadow: status === 'tracking' ? '0 0 40px rgba(0,255,255,0.5), 0 0 80px rgba(0,255,255,0.25)' : '0 10px 50px rgba(0,0,0,0.6)'
      }}
      initial={{ opacity: 0, scale: 0.5, y: 50 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.5, y: 50 }}
      transition={{ type: 'spring', duration: 0.5 }}
      data-testid="webcam-preview"
    >
      <video
        ref={videoRef}
        className="w-full h-full object-cover"
        style={{ transform: 'scaleX(-1)', minWidth: '280px', minHeight: '280px' }}
        playsInline
        muted
        autoPlay
        data-testid="webcam-video"
      />
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ transform: 'scaleX(-1)' }}
      />
      
      <button 
        onClick={onClose}
        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/80 flex items-center justify-center text-white hover:bg-red-500 transition-colors z-10"
      >
        <X className="w-4 h-4" />
      </button>
      
      {/* Camera selector button */}
      <button 
        onClick={() => setShowCameraSelector(!showCameraSelector)}
        className="absolute top-3 right-14 w-8 h-8 rounded-full bg-black/80 flex items-center justify-center text-white hover:bg-cyan-500 hover:text-black transition-colors z-10"
        title="Switch Camera"
      >
        <Camera className="w-4 h-4" />
      </button>
      
      {/* Camera selector dropdown */}
      {showCameraSelector && availableCameras.length > 0 && (
        <motion.div
          className="absolute top-14 right-3 z-20 rounded-lg overflow-hidden"
          style={{
            background: 'rgba(10, 10, 25, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="px-3 py-2 border-b border-white/10">
            <span className="text-xs text-gray-400">Select Camera</span>
          </div>
          <div className="max-h-40 overflow-y-auto">
            {availableCameras.map((camera, index) => (
              <button
                key={camera.deviceId}
                onClick={() => onCameraChange(camera.deviceId)}
                className={`w-full px-3 py-2 text-left text-xs hover:bg-cyan-500/20 transition-colors ${
                  selectedCameraId === camera.deviceId ? 'bg-cyan-500/30 text-cyan-400' : 'text-white'
                }`}
              >
                {camera.label || `Camera ${index + 1}`}
              </button>
            ))}
          </div>
        </motion.div>
      )}
      
      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/80 text-sm text-cyan-400 font-mono z-10 font-bold">
        {fps} FPS
      </div>
      
      {/* Hovered part indicator */}
      {hoveredPart && (
        <motion.div 
          className="absolute top-12 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-purple-500/90 text-white text-xs font-bold z-10"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          {hoveredPart}
        </motion.div>
      )}
      
      <AnimatePresence>
        {isActive && status === 'loading' && (
          <motion.div 
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mb-3" />
            <span className="text-white text-sm font-medium">Loading AI...</span>
          </motion.div>
        )}
      </AnimatePresence>
      
      {gesture && gesture !== GestureType.NONE && (
        <motion.div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full bg-cyan-500 text-black text-sm font-bold z-10"
          style={{ boxShadow: '0 0 25px rgba(0,255,255,0.7)' }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          key={gesture}
        >
          {gesture.replace('_', ' ').toUpperCase()}
        </motion.div>
      )}
      
      {isActive && status === 'ready' && !landmarks?.length && (
        <motion.div 
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-blue-500/90 text-white text-sm z-10"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          ✋ Show hand
        </motion.div>
      )}
    </motion.div>
  );
};

// ============== GESTURE STATUS ==============
const GestureStatusIndicator = ({ status, gesture, fps }) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'off': return { color: 'text-gray-400', bg: 'bg-gray-500/20', text: 'Off', dot: 'bg-gray-500' };
      case 'loading': return { color: 'text-yellow-400', bg: 'bg-yellow-500/20', text: 'Loading...', dot: 'bg-yellow-500 animate-pulse' };
      case 'ready': return { color: 'text-blue-400', bg: 'bg-blue-500/20', text: 'Show Hand', dot: 'bg-blue-500 animate-pulse' };
      case 'tracking': return { color: 'text-cyan-400', bg: 'bg-cyan-500/20', text: gesture !== GestureType.NONE ? gesture.replace('_', ' ') : 'Tracking', dot: 'bg-cyan-400' };
      case 'error': return { color: 'text-red-400', bg: 'bg-red-500/20', text: 'Error', dot: 'bg-red-500' };
      default: return { color: 'text-gray-400', bg: 'bg-gray-500/20', text: 'Unknown', dot: 'bg-gray-500' };
    }
  };
  
  const { color, bg, text, dot } = getStatusInfo();
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${bg} text-xs`}>
      <div className={`w-2 h-2 rounded-full ${dot}`} />
      <span className={`${color} font-medium capitalize`}>{text}</span>
      {fps > 0 && <span className="text-muted-foreground font-mono">{fps}</span>}
    </div>
  );
};

// ============== GESTURE HUD ==============
const GestureHUD = ({ currentGesture, fps, sensitivity, onSensitivityChange }) => {
  const GESTURES = [
    { type: GestureType.OPEN_PALM, icon: Hand, label: 'Palm', action: 'Rotate' },
    { type: GestureType.PINCH, icon: ZoomIn, label: 'Pinch', action: 'Zoom' },
    { type: GestureType.CLOSED_FIST, icon: Move, label: 'Fist', action: 'Pan' },
    { type: GestureType.POINTING, icon: Pointer, label: 'Point', action: 'Select' },
    { type: GestureType.SPREAD, icon: Layers, label: 'Spread', action: 'Explode' }
  ];
  
  return (
    <motion.div 
      className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-auto"
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
    >
      <div className="flex gap-2">
        {GESTURES.map(({ type, icon: Icon, label, action }) => {
          const isActive = currentGesture === type;
          return (
            <motion.div 
              key={type} 
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                isActive 
                  ? 'border-cyan-400 bg-cyan-500/30 text-cyan-300' 
                  : 'border-border bg-card/80 text-muted-foreground'
              }`}
              style={isActive ? { boxShadow: '0 0 20px rgba(0,255,255,0.4)' } : {}}
              animate={isActive ? { scale: [1, 1.08, 1] } : { scale: 1 }}
              transition={{ duration: 0.3, repeat: isActive ? Infinity : 0, repeatDelay: 0.5 }}
            >
              <Icon className="w-5 h-5" />
              <div className="flex flex-col">
                <span className="text-xs font-bold">{label}</span>
                <span className="text-[10px] opacity-70">{action}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      <div className="flex items-center gap-4 px-4 py-2 rounded-full bg-card/90 backdrop-blur border border-border">
        <div className="text-xs font-mono text-cyan-400 font-bold">{fps} FPS</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Speed</span>
          <input 
            type="range" 
            min="0.5" 
            max="2" 
            step="0.1" 
            value={sensitivity} 
            onChange={(e) => onSensitivityChange(parseFloat(e.target.value))} 
            className="w-24 accent-cyan-400"
          />
          <span className="text-xs font-mono text-cyan-400 w-10">{sensitivity.toFixed(1)}x</span>
        </div>
      </div>
    </motion.div>
  );
};

// ============== TEST PANEL ==============
const TestPanel = ({ onTestRotate, onTestZoom, onTestPan, onReset }) => (
  <motion.div 
    className="absolute top-20 right-4 p-3 rounded-xl bg-card/95 backdrop-blur border border-border shadow-xl z-30"
    initial={{ x: 100, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
  >
    <div className="flex items-center gap-2 mb-3 text-sm font-bold text-foreground">
      <TestTube2 className="w-4 h-4 text-cyan-400" />
      Test Controls
    </div>
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onTestRotate(-1)} title="Rotate Left">
          <RotateCcw className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onTestRotate(1)} title="Rotate Right">
          <RotateCw className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onTestZoom(1)} title="Zoom In">
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onTestZoom(-1)} title="Zoom Out">
          <ZoomOut className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onTestPan(-1, 0)} title="Pan Left">
          <ArrowLeftRight className="w-4 h-4" style={{ transform: 'scaleX(-1)' }} />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onTestPan(1, 0)} title="Pan Right">
          <ArrowLeftRight className="w-4 h-4" />
        </Button>
      </div>
      <Button size="sm" variant="secondary" onClick={onReset} className="mt-1">
        <RotateCcw className="w-4 h-4 mr-1" />
        Reset
      </Button>
    </div>
  </motion.div>
);

// ============== MAIN COMPONENT ==============
export const POCViewer = () => {
  // States
  const [wireframe, setWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [modelType, setModelType] = useState('gear');
  const [explodeAmount, setExplodeAmount] = useState(0);
  const [showPermissionModal, setShowPermissionModal] = useState(true);
  const [showModelBrowser, setShowModelBrowser] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [modalStatus, setModalStatus] = useState('idle');
  const [measureMode, setMeasureMode] = useState(false);
  const [showExplodePanel, setShowExplodePanel] = useState(false);
  const [measurementType, setMeasurementType] = useState('linear');
  const [unit, setUnit] = useState('mm');
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [gestureEnabled, setGestureEnabled] = useState(false);
  const [gestureStatus, setGestureStatus] = useState('off');
  const [gesture, setGesture] = useState(GestureType.NONE);
  const [landmarks, setLandmarks] = useState([]);
  const [fps, setFps] = useState(0);
  const [sensitivity, setSensitivity] = useState(1.0);
  const [cameraError, setCameraError] = useState(null);
  const [hoveredPart, setHoveredPart] = useState(null);
  const [models, setModels] = useState([]);
  const [samples, setSamples] = useState([]);
  const [isLoadingEngine, setIsLoadingEngine] = useState(false);
  const [engineInfo, setEngineInfo] = useState(null);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [engineParts, setEngineParts] = useState([]);
  const [renderQuality, setRenderQuality] = useState('clean'); // 'clean' or 'stylized'
  const [showGeminiPanel, setShowGeminiPanel] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [showPartIdentifier, setShowPartIdentifier] = useState(false);
  // Section cut state
  const [showSectionPanel, setShowSectionPanel] = useState(false);
  const [sectionAxis, setSectionAxis] = useState('z');
  const [sectionPosition, setSectionPosition] = useState(0);
  // Camera selection state
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [showCameraSelector, setShowCameraSelector] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const handLandmarkerRef = useRef(null);
  const animationRef = useRef(null);
  const viewer3DRef = useRef(null);
  const fpsCounterRef = useRef({ frames: 0, lastTime: performance.now() });
  const isInitializingRef = useRef(false);
  
  // Tracking state refs (for frame-to-frame comparison)
  const prevPalmCenterRef = useRef(null);
  const prevPinchDistRef = useRef(null);
  const prevFingerSpreadRef = useRef(null);
  const smootherRef = useRef(new ExponentialSmoother(0.35)); // Smoothing factor
  
  // Deadzone threshold (ignore tiny movements)
  const DEADZONE = 0.008;
  
  // DRAMATICALLY INCREASED MULTIPLIERS
  const ROTATE_MULTIPLIER = 8;    // Large value for full rotation
  const ZOOM_MULTIPLIER = 3;      // Exponential zoom
  const PAN_MULTIPLIER = 4;       // Smooth panning
  const SPIN_MULTIPLIER = 2;      // Model spinning
  
  // Dynamic parts list based on model type
  const parts = modelType === 'gear' 
    ? ['gear_main', 'gear_medium', 'gear_small']
    : modelType === 'piston'
    ? ['cylinder', 'piston_head', 'connecting_rod', 'crankshaft']
    : modelType === 'engine'
    ? engineParts
    : ['gear_main', 'piston'];
  
  // Load engine model
  const handleLoadEngine = useCallback(async (modelUrl = CAR_ENGINE_URL) => {
    if (isLoadingEngine) return;
    
    setIsLoadingEngine(true);
    setModelType('engine');
    
    try {
      const result = await viewer3DRef.current?.loadModel(modelUrl);
      if (result?.success) {
        console.log('[Engine] Loaded successfully:', result.partCount, 'parts');
        setEngineInfo(result);
      } else {
        console.error('[Engine] Failed to load:', result?.error);
      }
    } catch (err) {
      console.error('[Engine] Load error:', err);
    } finally {
      setIsLoadingEngine(false);
    }
  }, [isLoadingEngine]);
  
  // Handle model loaded callback
  const handleModelLoaded = useCallback((info) => {
    console.log('[POCViewer] Model loaded:', info);
    setEngineInfo(info);
    if (info.partNames) {
      setEngineParts(info.partNames.slice(0, 20)); // Limit to 20 parts for UI
    }
    // Reset animation state when new model is loaded
    setIsAnimationPlaying(false);
  }, []);
  
  // Toggle animation
  const handleToggleAnimation = useCallback(() => {
    const newState = !isAnimationPlaying;
    viewer3DRef.current?.toggleAnimation(newState);
    setIsAnimationPlaying(newState);
  }, [isAnimationPlaying]);
  
  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const [modelsRes, samplesRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/models`),
          fetch(`${BACKEND_URL}/api/models/samples`)
        ]);
        if (modelsRes.ok) setModels(await modelsRes.json());
        if (samplesRes.ok) setSamples(await samplesRes.json());
      } catch (err) {
        console.error('[Models] Load failed:', err);
      }
    };
    loadModels();
  }, []);
  
  // Initialize MediaPipe
  const initHandLandmarker = useCallback(async () => {
    console.log('[MediaPipe] Initializing...');
    
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );
      
      let handLandmarker;
      try {
        handLandmarker = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
            delegate: 'GPU'
          },
          numHands: 1,
          runningMode: 'VIDEO',
          minHandDetectionConfidence: 0.5,
          minHandPresenceConfidence: 0.5,
          minTrackingConfidence: 0.5
        });
        console.log('[MediaPipe] GPU mode');
      } catch {
        handLandmarker = await HandLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
            delegate: 'CPU'
          },
          numHands: 1,
          runningMode: 'VIDEO',
          minHandDetectionConfidence: 0.5,
          minHandPresenceConfidence: 0.5,
          minTrackingConfidence: 0.5
        });
        console.log('[MediaPipe] CPU mode');
      }
      
      handLandmarkerRef.current = handLandmarker;
      console.log('[MediaPipe] Ready');
      return true;
    } catch (err) {
      console.error('[MediaPipe] Failed:', err);
      throw err;
    }
  }, []);
  
  // Gesture history for stabilization
  const gestureHistoryRef = useRef([]);
  const getStableGesture = useCallback((rawGesture) => {
    gestureHistoryRef.current.push(rawGesture);
    if (gestureHistoryRef.current.length > 4) gestureHistoryRef.current.shift();
    
    const counts = {};
    gestureHistoryRef.current.forEach(g => { counts[g] = (counts[g] || 0) + 1; });
    
    let maxGesture = GestureType.NONE;
    let maxCount = 0;
    for (const [g, count] of Object.entries(counts)) {
      if (count > maxCount && count >= 2) {
        maxGesture = g;
        maxCount = count;
      }
    }
    return maxGesture;
  }, []);
  
  // Calculate palm center (average of key points)
  const getPalmCenter = useCallback((lm) => {
    if (!lm || lm.length < 18) return null;
    return {
      x: (lm[0].x + lm[5].x + lm[9].x + lm[13].x + lm[17].x) / 5,
      y: (lm[0].y + lm[5].y + lm[9].y + lm[13].y + lm[17].y) / 5
    };
  }, []);
  
  // Calculate pinch distance
  const getPinchDistance = useCallback((lm) => {
    if (!lm || lm.length < 9) return null;
    return Math.hypot(lm[4].x - lm[8].x, lm[4].y - lm[8].y);
  }, []);
  
  // Get index finger tip position for pointing
  const getIndexTip = useCallback((lm) => {
    if (!lm || lm.length < 9) return null;
    return { x: lm[8].x, y: lm[8].y };
  }, []);
  
  // Main frame processing loop
  const processFrame = useCallback(() => {
    const video = videoRef.current;
    const handLandmarker = handLandmarkerRef.current;
    
    // FPS counter
    const now = performance.now();
    fpsCounterRef.current.frames++;
    if (now - fpsCounterRef.current.lastTime >= 1000) {
      setFps(fpsCounterRef.current.frames);
      fpsCounterRef.current.frames = 0;
      fpsCounterRef.current.lastTime = now;
    }
    
    // Check prerequisites
    if (!video || !handLandmarker || video.readyState < 2) {
      animationRef.current = requestAnimationFrame(processFrame);
      return;
    }
    
    try {
      const results = handLandmarker.detectForVideo(video, now);
      
      if (results.landmarks && results.landmarks.length > 0) {
        const currentLandmarks = results.landmarks[0];
        setLandmarks(currentLandmarks);
        setGestureStatus('tracking');
        
        // Recognize gesture
        const { gesture: rawGesture, fingerSpread } = recognizeGesture(currentLandmarks);
        const stableGesture = getStableGesture(rawGesture);
        setGesture(stableGesture);
        
        // Calculate current values
        const currentPalmCenter = getPalmCenter(currentLandmarks);
        const currentPinchDist = getPinchDistance(currentLandmarks);
        const indexTip = getIndexTip(currentLandmarks);
        
        // Initialize raw deltas
        let rawDelta = { x: 0, y: 0, zoom: 0, spin: 0 };
        
        // Apply gesture-specific control
        if (viewer3DRef.current && stableGesture !== GestureType.NONE) {
          
          // OPEN PALM → ROTATE
          if (stableGesture === GestureType.OPEN_PALM && currentPalmCenter && prevPalmCenterRef.current) {
            const dx = (currentPalmCenter.x - prevPalmCenterRef.current.x);
            const dy = (currentPalmCenter.y - prevPalmCenterRef.current.y);
            
            rawDelta.x = dx * ROTATE_MULTIPLIER * sensitivity;
            rawDelta.y = dy * ROTATE_MULTIPLIER * sensitivity;
          }
          
          // PINCH → ZOOM (exponential based on distance ratio)
          else if (stableGesture === GestureType.PINCH && currentPinchDist !== null && prevPinchDistRef.current !== null) {
            // Use ratio for exponential zoom feel
            const ratio = prevPinchDistRef.current / currentPinchDist;
            const zoomDelta = (ratio - 1) * ZOOM_MULTIPLIER * sensitivity;
            rawDelta.zoom = zoomDelta;
          }
          
          // FIST → PAN
          else if (stableGesture === GestureType.CLOSED_FIST && currentPalmCenter && prevPalmCenterRef.current) {
            const dx = (currentPalmCenter.x - prevPalmCenterRef.current.x);
            const dy = (currentPalmCenter.y - prevPalmCenterRef.current.y);
            
            rawDelta.x = dx * PAN_MULTIPLIER * sensitivity;
            rawDelta.y = dy * PAN_MULTIPLIER * sensitivity;
          }
          
          // SPREAD → EXPLODE (increase/decrease based on spread width)
          else if (stableGesture === GestureType.SPREAD && prevFingerSpreadRef.current !== null) {
            const spreadDelta = fingerSpread - prevFingerSpreadRef.current;
            // Use spread delta to control explosion amount
            const newExplode = Math.max(0, Math.min(1, explodeAmount + spreadDelta * 3 * sensitivity));
            if (Math.abs(spreadDelta) > 0.005) {
              console.log('[GESTURE] EXPLODE:', spreadDelta.toFixed(4), '-> amount:', newExplode.toFixed(2));
              setExplodeAmount(newExplode);
            }
          }
          
          // POINTING → SELECT with laser
          else if (stableGesture === GestureType.POINTING && indexTip) {
            const hit = viewer3DRef.current.updateLaserPointer(indexTip);
            if (hit) {
              viewer3DRef.current.highlightPart(hit.object);
              setHoveredPart(hit.partName);
            } else {
              viewer3DRef.current.clearHighlight();
              setHoveredPart(null);
            }
          }
          
          // Apply smoothed deltas (except for pointing)
          if (stableGesture !== GestureType.POINTING) {
            // Clear laser when not pointing
            viewer3DRef.current.updateLaserPointer(null);
            viewer3DRef.current.clearHighlight();
            setHoveredPart(null);
            
            // Apply exponential smoothing
            const smoothed = smootherRef.current.update(rawDelta);
            
            // Apply deadzone
            const applyDeadzone = (val) => Math.abs(val) < DEADZONE ? 0 : val;
            smoothed.x = applyDeadzone(smoothed.x);
            smoothed.y = applyDeadzone(smoothed.y);
            smoothed.zoom = applyDeadzone(smoothed.zoom);
            smoothed.spin = applyDeadzone(smoothed.spin);
            
            // Apply to 3D viewer
            if (stableGesture === GestureType.OPEN_PALM && (smoothed.x !== 0 || smoothed.y !== 0)) {
              console.log('[GESTURE] ROTATE:', smoothed.x.toFixed(4), smoothed.y.toFixed(4));
              viewer3DRef.current.applyGestureControl('rotate', smoothed);
            }
            else if (stableGesture === GestureType.PINCH && smoothed.zoom !== 0) {
              console.log('[GESTURE] ZOOM:', smoothed.zoom.toFixed(4));
              viewer3DRef.current.applyGestureControl('zoom', smoothed);
            }
            else if (stableGesture === GestureType.CLOSED_FIST && (smoothed.x !== 0 || smoothed.y !== 0)) {
              console.log('[GESTURE] PAN:', smoothed.x.toFixed(4), smoothed.y.toFixed(4));
              viewer3DRef.current.applyGestureControl('pan', smoothed);
            }
            else if (stableGesture === GestureType.SPREAD && smoothed.spin !== 0) {
              console.log('[GESTURE] SPIN:', smoothed.spin.toFixed(4));
              viewer3DRef.current.applyGestureControl('spin', smoothed);
            }
          }
        }
        
        // Store values for next frame
        prevPalmCenterRef.current = currentPalmCenter;
        prevPinchDistRef.current = currentPinchDist;
        prevFingerSpreadRef.current = fingerSpread;
        
      } else {
        // No hand detected
        setLandmarks([]);
        setGesture(GestureType.NONE);
        setGestureStatus('ready');
        prevPalmCenterRef.current = null;
        prevPinchDistRef.current = null;
        prevFingerSpreadRef.current = null;
        smootherRef.current.reset();
        setHoveredPart(null);
        
        // Clear laser
        if (viewer3DRef.current) {
          viewer3DRef.current.updateLaserPointer(null);
          viewer3DRef.current.clearHighlight();
        }
      }
    } catch (err) {
      console.error('[Frame] Error:', err);
    }
    
    animationRef.current = requestAnimationFrame(processFrame);
  }, [sensitivity, getStableGesture, getPalmCenter, getPinchDistance, getIndexTip]);
  
  // Enumerate available cameras
  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      console.log('[Camera] Available cameras:', cameras.map(c => c.label || c.deviceId));
      setAvailableCameras(cameras);
      return cameras;
    } catch (err) {
      console.error('[Camera] Failed to enumerate devices:', err);
      return [];
    }
  }, []);
  
  // Switch camera
  const switchCamera = useCallback(async (deviceId) => {
    console.log('[Camera] Switching to camera:', deviceId);
    
    // Stop current stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('[Camera] Stopped track:', track.label);
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    let stream = null;
    let lastError = null;
    
    // Method 1: Try exact constraint
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false
      });
      console.log('[Camera] Switch method 1 (exact) succeeded');
    } catch (e1) {
      console.log('[Camera] Switch method 1 failed:', e1.message);
      lastError = e1;
    }
    
    // Method 2: Try direct deviceId
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: deviceId },
          audio: false
        });
        console.log('[Camera] Switch method 2 (direct) succeeded');
      } catch (e2) {
        console.log('[Camera] Switch method 2 failed:', e2.message);
        lastError = e2;
      }
    }
    
    // Method 3: Try ideal constraint
    if (!stream) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { ideal: deviceId } },
          audio: false
        });
        console.log('[Camera] Switch method 3 (ideal) succeeded');
      } catch (e3) {
        console.log('[Camera] Switch method 3 failed:', e3.message);
        lastError = e3;
      }
    }
    
    if (stream) {
      const activeTrack = stream.getVideoTracks()[0];
      console.log('[Camera] Successfully switched to:', activeTrack?.label);
      
      streamRef.current = stream;
      setSelectedCameraId(deviceId);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setShowCameraSelector(false);
      setCameraError(null);
    } else {
      console.error('[Camera] All switch methods failed');
      setCameraError('Failed to access selected camera. Try closing other apps using the camera.');
    }
  }, []);
  
  // Start gestures
  const startGestures = useCallback(async (cameraId = null) => {
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;
    
    setModalStatus('loading');
    setGestureStatus('loading');
    setCameraError(null);
    
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera API not supported');
      }
      
      // Enumerate cameras first
      await enumerateCameras();
      
      console.log('[Camera] Requesting camera access...');
      const targetDeviceId = cameraId || selectedCameraId;
      
      let stream = null;
      let lastError = null;
      
      if (targetDeviceId) {
        console.log('[Camera] Attempting to use selected camera:', targetDeviceId);
        
        // Method 1: Try with exact constraint (works for most cameras)
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: targetDeviceId } },
            audio: false
          });
          console.log('[Camera] Method 1 (exact) succeeded');
        } catch (e1) {
          console.log('[Camera] Method 1 (exact) failed:', e1.message);
          lastError = e1;
        }
        
        // Method 2: Try with just deviceId (more forgiving for virtual cameras)
        if (!stream) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: targetDeviceId },
              audio: false
            });
            console.log('[Camera] Method 2 (direct) succeeded');
          } catch (e2) {
            console.log('[Camera] Method 2 (direct) failed:', e2.message);
            lastError = e2;
          }
        }
        
        // Method 3: Try with ideal constraint
        if (!stream) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: { deviceId: { ideal: targetDeviceId } },
              audio: false
            });
            console.log('[Camera] Method 3 (ideal) succeeded');
          } catch (e3) {
            console.log('[Camera] Method 3 (ideal) failed:', e3.message);
            lastError = e3;
          }
        }
      }
      
      // Method 4: Fall back to default camera
      if (!stream) {
        console.log('[Camera] Falling back to default camera');
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
          console.log('[Camera] Method 4 (default) succeeded');
        } catch (e4) {
          console.log('[Camera] Method 4 (default) failed:', e4.message);
          throw lastError || e4;
        }
      }
      
      const activeCamera = stream.getVideoTracks()[0];
      console.log('[Camera] Stream obtained:', activeCamera?.label);
      
      // Set the selected camera ID from the actual stream
      if (activeCamera) {
        const settings = activeCamera.getSettings();
        if (settings.deviceId) {
          setSelectedCameraId(settings.deviceId);
        }
      }
      streamRef.current = stream;
      
      // Initialize MediaPipe first
      await initHandLandmarker();
      console.log('[Camera] MediaPipe initialized');
      
      // Hide modal and enable gesture mode - this will render the WebcamPreview
      setShowPermissionModal(false);
      setGestureEnabled(true);
      
      // IMPORTANT: Wait for React to render the WebcamPreview component
      // This ensures videoRef.current points to the actual DOM element
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now set up the video element - retry a few times if needed
      let retries = 0;
      const maxRetries = 10;
      while (!videoRef.current && retries < maxRetries) {
        console.log('[Camera] Waiting for video element... attempt', retries + 1);
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }
      
      if (!videoRef.current) {
        throw new Error('Video element not available');
      }
      
      console.log('[Camera] Video element found, setting srcObject');
      videoRef.current.srcObject = stream;
      
      // Wait for video to be ready to play
      await new Promise((resolve, reject) => {
        const video = videoRef.current;
        const timeout = setTimeout(() => reject(new Error('Video load timeout')), 10000);
        
        const handleCanPlay = () => {
          console.log('[Camera] Video can play, starting playback');
          clearTimeout(timeout);
          video.removeEventListener('canplay', handleCanPlay);
          video.play()
            .then(() => {
              console.log('[Camera] Video playing successfully');
              resolve();
            })
            .catch(reject);
        };
        
        // Check if already ready
        if (video.readyState >= 3) {
          handleCanPlay();
        } else {
          video.addEventListener('canplay', handleCanPlay);
          // Also try to play immediately (some browsers need this)
          video.play().catch(() => {});
        }
      });
      
      setGestureStatus('ready');
      setModalStatus('idle');
      console.log('[Camera] Starting frame processing');
      animationRef.current = requestAnimationFrame(processFrame);
      
    } catch (err) {
      console.error('[Camera] Error:', err);
      setCameraError(err.message || 'Failed to access camera');
      setGestureStatus('error');
      setModalStatus('error');
      setGestureEnabled(false);
    } finally {
      isInitializingRef.current = false;
    }
  }, [initHandLandmarker, processFrame]);
  
  // Stop gestures
  const stopGestures = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    
    setGestureEnabled(false);
    setGestureStatus('off');
    setLandmarks([]);
    setGesture(GestureType.NONE);
    setFps(0);
    setHoveredPart(null);
    prevPalmCenterRef.current = null;
    prevPinchDistRef.current = null;
    prevFingerSpreadRef.current = null;
    smootherRef.current.reset();
    gestureHistoryRef.current = [];
  }, []);
  
  const toggleGestures = useCallback(async () => {
    if (gestureEnabled) stopGestures();
    else await startGestures();
  }, [gestureEnabled, startGestures, stopGestures]);
  
  // Enumerate cameras when modal opens
  useEffect(() => {
    if (showPermissionModal && availableCameras.length === 0) {
      enumerateCameras();
    }
  }, [showPermissionModal, availableCameras.length, enumerateCameras]);
  
  // Handlers
  const handleSkipCamera = useCallback(() => {
    setShowPermissionModal(false);
    setGestureEnabled(false);
    setGestureStatus('off');
  }, []);
  
  const handleRetryCamera = useCallback(() => {
    setModalStatus('idle');
    setCameraError(null);
  }, []);
  
  const handleResetCamera = useCallback(() => {
    viewer3DRef.current?.resetCamera();
  }, []);
  
  const handleTestRotate = useCallback((dir) => viewer3DRef.current?.testRotate(dir), []);
  const handleTestZoom = useCallback((dir) => viewer3DRef.current?.testZoom(dir), []);
  const handleTestPan = useCallback((dx, dy) => viewer3DRef.current?.testPan(dx, dy), []);
  
  // Measurement handlers
  const handleMeasurementClick = useCallback((point) => {
    setSelectedPoints(prev => {
      const newPoints = [...prev, point];
      viewer3DRef.current?.addMeasurementMarker(point);
      
      const requiredPoints = measurementType === 'angular' ? 3 : 2;
      if (newPoints.length >= requiredPoints) {
        if (measurementType !== 'angular') {
          viewer3DRef.current?.addMeasurementLine(newPoints[0], newPoints[1]);
        }
        
        let value;
        if (measurementType === 'angular' && newPoints.length >= 3) {
          const v1 = { x: newPoints[0].x - newPoints[1].x, y: newPoints[0].y - newPoints[1].y, z: newPoints[0].z - newPoints[1].z };
          const v2 = { x: newPoints[2].x - newPoints[1].x, y: newPoints[2].y - newPoints[1].y, z: newPoints[2].z - newPoints[1].z };
          const dot = v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
          const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y + v1.z * v1.z);
          const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y + v2.z * v2.z);
          value = Math.acos(Math.max(-1, Math.min(1, dot / (mag1 * mag2)))) * (180 / Math.PI);
        } else {
          const dx = newPoints[1].x - newPoints[0].x;
          const dy = newPoints[1].y - newPoints[0].y;
          const dz = newPoints[1].z - newPoints[0].z;
          value = Math.sqrt(dx * dx + dy * dy + dz * dz);
        }
        
        setMeasurements(prev => [...prev, { id: Date.now().toString(), measurement_type: measurementType, value, unit, points: newPoints }]);
        return [];
      }
      return newPoints;
    });
  }, [measurementType, unit]);
  
  const handleClearPoints = useCallback(() => {
    setSelectedPoints([]);
    viewer3DRef.current?.clearMeasurements();
  }, []);
  
  const handleDeleteMeasurement = useCallback((id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
  }, []);
  
  // Model handlers
  const handleUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/models/upload`, { method: 'POST', body: formData });
      if (res.ok) {
        const uploaded = await res.json();
        setModels(prev => [...prev, uploaded]);
      }
    } catch (err) {
      console.error('[Upload] Failed:', err);
    }
  }, []);
  
  const handleSelectModel = useCallback((model) => {
    if (model.file_type === 'builtin') {
      const type = model.id.replace('sample-', '').replace('-assembly', 'both');
      setModelType(type === 'gear-assembly' ? 'gear' : type);
    }
    setShowModelBrowser(false);
  }, []);
  
  const handleDeleteModel = useCallback(async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/models/${id}`, { method: 'DELETE' });
      setModels(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      console.error('[Delete] Failed:', err);
    }
  }, []);
  
  // Cleanup
  useEffect(() => {
    return () => {
      stopGestures();
      handLandmarkerRef.current?.close();
    };
  }, [stopGestures]);
  
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background" data-testid="poc-viewer">
      <CameraPermissionModal
        isOpen={showPermissionModal}
        onClose={handleSkipCamera}
        onRequestPermission={startGestures}
        onRetry={handleRetryCamera}
        status={modalStatus}
        error={cameraError}
        availableCameras={availableCameras}
        selectedCameraId={selectedCameraId}
        onCameraSelect={setSelectedCameraId}
      />
      
      <ModelBrowser
        isOpen={showModelBrowser}
        onClose={() => setShowModelBrowser(false)}
        models={models}
        samples={samples}
        onSelectModel={handleSelectModel}
        onUpload={handleUpload}
        onDelete={handleDeleteModel}
        isLoading={false}
      />
      
      {/* Glassmorphism Toolbar */}
      <motion.div 
        className="sticky top-0 z-40 flex items-center justify-between h-11 px-3 bg-background/70 backdrop-blur-xl border-b border-white/5"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="flex items-center gap-2">
          <Box className="w-5 h-5 text-cyan-400" />
          <h1 className="text-sm font-semibold tracking-tight">MechLab XR</h1>
        </div>
        
        <div className="flex items-center gap-1">
          <Button variant={modelType === 'gear' ? 'default' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setModelType('gear')} data-testid="model-gear-btn">
            <Cog className="w-3 h-3 mr-1" />Gear
          </Button>
          <Button variant={modelType === 'piston' ? 'default' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setModelType('piston')} data-testid="model-piston-btn">
            <Box className="w-3 h-3 mr-1" />Piston
          </Button>
          <Button variant={modelType === 'both' ? 'default' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => setModelType('both')} data-testid="model-assembly-btn">
            Assembly
          </Button>
          <Button 
            variant={modelType === 'engine' ? 'default' : 'ghost'} 
            size="sm"
            className={`h-7 px-2 text-xs ${modelType === 'engine' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            onClick={() => handleLoadEngine(CAR_ENGINE_URL)}
            data-testid="model-engine-btn"
          >
            <Car className="w-3 h-3 mr-1" />
            {isLoadingEngine ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Engine'}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => handleLoadEngine(V8_ENGINE_URL)}
            data-testid="model-v8-btn"
          >
            <Zap className="w-3 h-3 mr-1" />
            V8
          </Button>
          
          {/* Animation Play/Pause button - only show when engine has animations */}
          {modelType === 'engine' && engineInfo?.hasAnimations && (
            <Button 
              variant={isAnimationPlaying ? 'default' : 'outline'} 
              size="sm"
              className={`h-7 px-2 text-xs ${isAnimationPlaying ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={handleToggleAnimation}
              data-testid="animation-toggle-btn"
            >
              {isAnimationPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            </Button>
          )}
          
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setShowModelBrowser(true)} data-testid="model-browser-btn">
            <Folder className="w-3 h-3" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Engine info (compact) */}
          {modelType === 'engine' && engineInfo && (
            <div className="hidden md:flex items-center gap-1.5 px-2 py-0.5 rounded bg-cyan-500/10 text-[10px] font-mono">
              <span className="text-cyan-400">{engineInfo.partCount}p</span>
              <span className="text-muted-foreground">{(engineInfo.vertexCount / 1000).toFixed(0)}k</span>
            </div>
          )}
          
          {/* Render Quality Dropdown */}
          <div className="flex items-center gap-0.5 bg-muted/50 rounded-md p-0.5">
            <Button 
              variant={renderQuality === 'performance' ? 'default' : 'ghost'} 
              size="sm"
              className={`h-6 px-2 text-[10px] ${renderQuality === 'performance' ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={() => setRenderQuality('performance')}
              data-testid="render-perf-btn"
              title="Performance mode (no shadows/bloom)"
            >
              <Zap className="w-3 h-3" />
            </Button>
            <Button 
              variant={renderQuality === 'clean' ? 'default' : 'ghost'} 
              size="sm"
              className={`h-6 px-2 text-[10px] ${renderQuality === 'clean' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              onClick={() => setRenderQuality('clean')}
              data-testid="render-clean-btn"
              title="Clean CAD mode"
            >
              Clean
            </Button>
            <Button 
              variant={renderQuality === 'stylized' ? 'default' : 'ghost'} 
              size="sm"
              className={`h-6 px-2 text-[10px] ${renderQuality === 'stylized' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
              onClick={() => setRenderQuality('stylized')}
              data-testid="render-style-btn"
              title="Stylized mode"
            >
              <Sparkles className="w-3 h-3" />
            </Button>
          </div>
          
          <div className="h-5 w-px bg-border/50" />
          
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleResetCamera} data-testid="reset-camera-btn">
            <RotateCcw className="w-3 h-3" />
          </Button>
          <Button variant={measureMode ? 'default' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => { setMeasureMode(m => !m); setShowExplodePanel(false); }}>
            <Ruler className="w-3 h-3" />
          </Button>
          <Button variant={showExplodePanel ? 'default' : 'ghost'} size="sm" className="h-7 px-2" onClick={() => { setShowExplodePanel(e => !e); setMeasureMode(false); }}>
            <Layers className="w-3 h-3" />
          </Button>
          
          {/* Section Cut Button */}
          <Button 
            variant={showSectionPanel ? 'default' : 'ghost'} 
            size="sm" 
            className={`h-7 px-2 ${showSectionPanel ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            onClick={() => {
              const newState = !showSectionPanel;
              setShowSectionPanel(newState);
              if (viewer3DRef.current?.toggleSectionPlane) {
                viewer3DRef.current.toggleSectionPlane(newState);
              }
            }}
            data-testid="section-btn"
          >
            <Scissors className="w-3 h-3" />
          </Button>
          
          <div className="h-5 w-px bg-border/50" />
          
          <div className="flex items-center gap-1.5">
            <Switch id="grid" checked={showGrid} onCheckedChange={setShowGrid} className="scale-75" />
            <Label htmlFor="grid" className="text-[10px]">Grid</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <Switch id="wire" checked={wireframe} onCheckedChange={setWireframe} className="scale-75" />
            <Label htmlFor="wire" className="text-[10px]">Wire</Label>
          </div>
          
          <div className="h-5 w-px bg-border/50" />
          
          <Button 
            variant={gestureEnabled ? 'default' : 'outline'} 
            size="sm"
            className={`h-7 px-2 text-xs ${gestureEnabled ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            onClick={toggleGestures}
          >
            {gestureEnabled ? <Video className="w-3 h-3 mr-1" /> : <VideoOff className="w-3 h-3 mr-1" />}
            Gestures
          </Button>
          <GestureStatusIndicator status={gestureStatus} gesture={gesture} fps={fps} />
          
          <div className="h-5 w-px bg-border/50" />
          
          {/* Guided Tour Button */}
          <Button 
            variant={showGuidedTour ? 'default' : 'outline'} 
            size="sm"
            className={`h-7 px-3 text-xs font-medium ${showGuidedTour ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 border-0' : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'}`}
            onClick={() => setShowGuidedTour(!showGuidedTour)}
            data-testid="guided-tour-btn"
            style={!showGuidedTour ? { boxShadow: '0 0 10px rgba(0, 212, 255, 0.15)' } : {}}
          >
            <span className="mr-1">✦</span>
            Tour
          </Button>
          
          {/* Part Identifier Button */}
          <Button 
            variant={showPartIdentifier ? 'default' : 'outline'} 
            size="sm"
            className={`h-7 px-3 text-xs font-medium ${showPartIdentifier ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 border-0' : 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10'}`}
            onClick={() => setShowPartIdentifier(!showPartIdentifier)}
            data-testid="part-identifier-btn"
            style={!showPartIdentifier ? { boxShadow: '0 0 10px rgba(168, 85, 247, 0.15)' } : {}}
          >
            <span className="mr-1">✦</span>
            Identify
          </Button>
          
          {/* Gemini AI Button */}
          <Button 
            variant={showGeminiPanel ? 'default' : 'outline'} 
            size="sm"
            className={`h-7 px-3 text-xs font-medium ${showGeminiPanel ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 border-0' : 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'}`}
            onClick={() => setShowGeminiPanel(!showGeminiPanel)}
            data-testid="gemini-btn"
            style={!showGeminiPanel ? { boxShadow: '0 0 10px rgba(0, 212, 255, 0.2)' } : {}}
          >
            <span className="mr-1">✦</span>
            Ask Gemini
          </Button>
        </div>
      </motion.div>
      
      {/* Main content */}
      <div className="flex-1 relative">
        <Viewer3D
          ref={viewer3DRef}
          wireframe={wireframe}
          showGrid={showGrid}
          modelType={modelType}
          explodeAmount={explodeAmount}
          measureMode={measureMode}
          onMeasurementClick={handleMeasurementClick}
          selectedMeasurementPoints={selectedPoints}
          onModelLoaded={handleModelLoaded}
          renderQuality={renderQuality}
        />
        
        {/* Guided Tour */}
        <GuidedTour
          isOpen={showGuidedTour}
          onClose={() => setShowGuidedTour(false)}
          viewerRef={viewer3DRef}
          modelInfo={{
            name: modelType === 'engine' ? 'Car Engine Assembly' : modelType === 'gear' ? 'Gear Assembly' : 'Mechanical Assembly'
          }}
        />
        
        {/* Part Identifier */}
        <PartIdentifier
          isOpen={showPartIdentifier}
          onClose={() => setShowPartIdentifier(false)}
          viewerRef={viewer3DRef}
          modelInfo={{
            name: modelType === 'engine' ? 'Car Engine Assembly' : modelType === 'gear' ? 'Gear Assembly' : 'Mechanical Assembly'
          }}
        />
        
        {/* Gemini AI Panel */}
        <GeminiPanel
          isOpen={showGeminiPanel}
          onClose={() => setShowGeminiPanel(false)}
          viewerRef={viewer3DRef}
          modelInfo={{
            name: modelType === 'engine' ? 'Car Engine Assembly' : modelType === 'gear' ? 'Gear Assembly' : modelType === 'piston' ? 'Piston' : 'Mechanical Assembly',
            partCount: engineInfo?.partCount || (modelType === 'gear' ? 3 : modelType === 'piston' ? 1 : 0),
            vertexCount: engineInfo?.vertexCount || 0,
            explodeLevel: explodeAmount
          }}
          onExplodeChange={(target, animated, duration) => {
            if (animated && viewer3DRef.current?.setExplodeAmount) {
              viewer3DRef.current.setExplodeAmount(target, true, duration);
              setTimeout(() => setExplodeAmount(target), duration);
            } else {
              setExplodeAmount(target);
            }
          }}
        />
        
        <MeasurementPanel
          isActive={measureMode}
          measurementType={measurementType}
          onMeasurementTypeChange={setMeasurementType}
          selectedPoints={selectedPoints}
          measurements={measurements}
          onClearPoints={handleClearPoints}
          onDeleteMeasurement={handleDeleteMeasurement}
          unit={unit}
          onUnitChange={setUnit}
        />
        <ExplodePanel
          isActive={showExplodePanel}
          explodeAmount={explodeAmount}
          onExplodeChange={setExplodeAmount}
          onAnimatedExplode={(target, duration) => {
            viewer3DRef.current?.setExplodeAmount(target, true, duration);
            // Also update local state after animation
            setTimeout(() => setExplodeAmount(target), duration);
          }}
          parts={parts}
          selectedPart={null}
          onPartSelect={() => {}}
        />
        
        {/* Section Panel */}
        <SectionPanel
          isActive={showSectionPanel}
          onClose={() => {
            setShowSectionPanel(false);
            if (viewer3DRef.current?.toggleSectionPlane) {
              viewer3DRef.current.toggleSectionPlane(false);
            }
          }}
          axis={sectionAxis}
          onAxisChange={(newAxis) => {
            setSectionAxis(newAxis);
            if (viewer3DRef.current?.setSectionAxis) {
              viewer3DRef.current.setSectionAxis(newAxis);
            }
          }}
          position={sectionPosition}
          onPositionChange={(newPos) => {
            setSectionPosition(newPos);
            if (viewer3DRef.current?.setSectionPosition) {
              viewer3DRef.current.setSectionPosition(newPos);
            }
          }}
          onReset={() => {
            setSectionPosition(0);
            if (viewer3DRef.current?.setSectionPosition) {
              viewer3DRef.current.setSectionPosition(0);
            }
          }}
        />
        
        <AnimatePresence>
          {showTestPanel && (
            <TestPanel
              onTestRotate={handleTestRotate}
              onTestZoom={handleTestZoom}
              onTestPan={handleTestPan}
              onReset={handleResetCamera}
            />
          )}
        </AnimatePresence>
        
        <div className="absolute inset-0 pointer-events-none">
          {gestureEnabled && gestureStatus !== 'off' && (
            <GestureHUD
              currentGesture={gesture}
              fps={fps}
              sensitivity={sensitivity}
              onSensitivityChange={setSensitivity}
            />
          )}
        </div>
        
        <AnimatePresence>
          {gestureEnabled && (
            <WebcamPreview
              videoRef={videoRef}
              canvasRef={canvasRef}
              isActive={gestureEnabled}
              landmarks={landmarks}
              gesture={gesture}
              status={gestureStatus}
              onClose={stopGestures}
              fps={fps}
              hoveredPart={hoveredPart}
              availableCameras={availableCameras}
              selectedCameraId={selectedCameraId}
              onCameraChange={switchCamera}
              showCameraSelector={showCameraSelector}
              setShowCameraSelector={setShowCameraSelector}
            />
          )}
        </AnimatePresence>
        
        {/* Zoom Buttons - bottom left */}
        <div className="absolute bottom-4 left-4 flex flex-col gap-2 z-30">
          <button
            onClick={() => {
              if (viewer3DRef.current?.applyGestureControl) {
                viewer3DRef.current.applyGestureControl('zoom', { zoom: 0.15 });
              }
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
            style={{
              background: 'rgba(10, 10, 25, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)'
            }}
            data-testid="zoom-in-btn"
          >
            +
          </button>
          <button
            onClick={() => {
              if (viewer3DRef.current?.applyGestureControl) {
                viewer3DRef.current.applyGestureControl('zoom', { zoom: -0.15 });
              }
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-gray-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
            style={{
              background: 'rgba(10, 10, 25, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)'
            }}
            data-testid="zoom-out-btn"
          >
            −
          </button>
        </div>
        
        {/* Compact gesture legend - bottom right, fades when inactive */}
        {gestureEnabled && (
          <motion.div
            className="absolute bottom-4 right-4 p-2 rounded-lg bg-background/60 backdrop-blur-sm border border-white/10 text-[10px]"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: gesture === 'none' ? 0.4 : 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col gap-1 text-muted-foreground">
              <div className={gesture === 'open' ? 'text-cyan-400' : ''}>✋ Rotate</div>
              <div className={gesture === 'pinch' ? 'text-cyan-400' : ''}>🤏 Zoom</div>
              <div className={gesture === 'fist' ? 'text-cyan-400' : ''}>✊ Pan</div>
              <div className={gesture === 'point' ? 'text-cyan-400' : ''}>👆 Select</div>
            </div>
          </motion.div>
        )}
        
        {!gestureEnabled && !measureMode && (
          <motion.div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-background/60 backdrop-blur-sm border border-white/10 text-[11px] text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.7, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Drag to rotate • Scroll to zoom • Right-drag to pan
          </motion.div>
        )}
        
        {measureMode && (
          <motion.div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-cyan-600/90 text-white text-[11px] font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Click on model to place measurement points
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default POCViewer;
